/**
 * One target, full-page - the view a card's "open in a new tab" leads to.
 *
 * It shares the dashboard's snapshot query, so a detail tab left open on a wall display
 * updates on the same poll as the grid, with no second polling path to keep in sync.
 * The difference from the card is room: the whole window rather than a filtered slice,
 * every service and queue family expanded, the raw payload open, and its own document
 * title so a row of pinned tabs is legible.
 *
 * A target id that is not in the snapshot is handled explicitly rather than rendered as
 * a blank: a manual monitor that was deleted, or a stale bookmark, should say so and
 * offer the way back, not look like a bug.
 */

import { ArrowLeft, ExternalLink, RefreshCw } from 'lucide-react'
import { useEffect, useState } from 'react'

import { StatusTimeline } from './components/charts/StatusTimeline'
import { Meter } from './components/charts/Meter'
import { QueuePanel } from './components/QueuePanel'
import { RawPayload } from './components/RawPayload'
import { ServiceList } from './components/ServiceList'
import { StatusPill } from './components/ui/StatusPill'
import { IntervalControl } from './components/ui/IntervalControl'
import { Header } from './components/Header'
import { useDashboard, useProbe, useSetInterval } from './hooks/useDashboard'
import { useTheme } from './hooks/useTheme'
import { cn } from './lib/cn'
import { formatDateTime, formatMs, formatPct, formatRelative } from './lib/format'
import { STATUS_PRESENTATION, latencyBand, latencyFill, resolveThresholds } from './lib/status'
import type { QueueSnapshot } from './api/types'

interface TargetDetailPageProps {
  targetId: string
  onBack: () => void
}

export function TargetDetailPage({ targetId, onBack }: TargetDetailPageProps) {
  const theme = useTheme()
  const probe = useProbe()
  const setTargetInterval = useSetInterval()
  const {
    snapshot,
    snapshotError,
    pollIntervalSeconds,
    thresholds,
    sitesById,
    isLoading,
    isRefetching,
  } = useDashboard(true)

  const result = snapshot?.results.find((entry) => entry.target_id === targetId)
  const history = snapshot?.history[targetId]
  // Carries this target's latency bands - its site's, and its per-check ones. Looked up
  // here so the page and the grid's card colour the same reading the same way.
  const site = result ? sitesById.get(result.site_id) : undefined

  // This target's own poll interval, once the backend confirms one; until then it shows
  // the global default. A picker on the page sets it (min 5s, "Default" clears it).
  const [interval, setInterval] = useState<{ seconds: number; custom: boolean } | null>(null)
  const effectiveInterval = interval?.seconds ?? pollIntervalSeconds

  const probeNow = () =>
    result && probe.mutate({ target_ids: [result.target_id] })

  // A per-target title, so a row of pinned monitoring tabs is legible.
  useEffect(() => {
    if (!result) return
    const previous = document.title
    document.title = `${STATUS_PRESENTATION[result.status].label} · ${result.site_name} ${result.env_label}`
    return () => {
      document.title = previous
    }
  }, [result])

  const queueSnapshot = result?.services.find(
    (service): service is typeof service & { data: QueueSnapshot } =>
      Boolean(service.data?.families && service.data?.totals),
  )?.data as QueueSnapshot | undefined

  const band = result
    ? latencyBand(result.elapsed_ms, resolveThresholds(thresholds, site))
    : null
  const presentation = result ? STATUS_PRESENTATION[result.status] : null
  const issueEntries = Object.entries(result?.issues ?? {})

  return (
    <div className="min-h-screen px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-5xl flex-col gap-4">
        <Header
          lastPollAt={snapshot?.last_poll_at ?? null}
          pollIntervalSeconds={effectiveInterval}
          autoRefresh
          // A single-target tab has no auto-refresh toggle - watching is its whole job.
          onAutoRefreshChange={() => {}}
          onRefresh={probeNow}
          isRefreshing={probe.isPending}
          theme={theme.resolved}
          onToggleTheme={theme.toggle}
          staleWarning={
            snapshotError ? `Cannot reach the dashboard API: ${snapshotError.message}` : null
          }
        />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <a
            href="#/"
            onClick={(event) => {
              // Let a modified click open the dashboard in yet another tab.
              if (event.metaKey || event.ctrlKey || event.button !== 0) return
              event.preventDefault()
              onBack()
            }}
            className="inline-flex w-fit items-center gap-1.5 text-xs text-ink-secondary hover:text-ink"
          >
            <ArrowLeft size={14} aria-hidden />
            All targets
          </a>

          {/* This target's own fetch interval - "Default" inherits the global one. */}
          {result ? (
            <IntervalControl
              label="Fetch every"
              seconds={effectiveInterval}
              isCustom={interval?.custom ?? false}
              allowDefault
              pending={setTargetInterval.isPending}
              onChange={(seconds) =>
                setTargetInterval
                  .mutateAsync({ targetId: result.target_id, seconds })
                  .then((s) =>
                    setInterval({
                      seconds: s.poll_interval_seconds,
                      custom: s.custom_interval,
                    }),
                  )
              }
            />
          ) : null}
        </div>

        {isLoading && !snapshot ? (
          <p className="card p-8 text-center text-sm text-ink-secondary">Loading…</p>
        ) : null}

        {snapshot && !result ? (
          <div className="card p-8 text-center">
            <p className="text-sm text-ink">This target is not being monitored.</p>
            <p className="mt-1 text-xs text-ink-secondary">
              It may have been a manual monitor that was removed, or the link may be
              stale. <code className="text-ink">{targetId}</code>
            </p>
            <button
              type="button"
              onClick={onBack}
              className="mt-3 inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs text-ink hover:bg-surface-sunken"
            >
              <ArrowLeft size={13} aria-hidden />
              Back to the dashboard
            </button>
          </div>
        ) : null}

        {result && presentation ? (
          <div className={cn('flex flex-col gap-4', isRefetching && 'refetching')}>
            <article
              className="card overflow-hidden"
              style={{ borderTopWidth: 3, borderTopColor: `var(--color-${presentation.token})` }}
            >
              <header className="flex flex-wrap items-start justify-between gap-4 p-5">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-xl font-semibold text-ink">{result.site_name}</h1>
                    <span className="rounded-full border px-2 py-0.5 text-xs text-ink-secondary">
                      {result.env_label}
                    </span>
                    {result.manual ? (
                      <span className="rounded-full border px-2 py-0.5 text-xs text-ink-muted">
                        manual
                      </span>
                    ) : null}
                    <StatusPill status={result.status} size="lg" />
                  </div>
                  <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-secondary">
                    <a
                      href={result.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="inline-flex items-center gap-1 hover:text-ink hover:underline"
                    >
                      {result.url}
                      <ExternalLink size={12} aria-hidden />
                    </a>
                    <span aria-hidden>·</span>
                    <span className="tabular">
                      {result.http_status === null ? 'no response' : `HTTP ${result.http_status}`}
                    </span>
                    <span aria-hidden>·</span>
                    <span>checked {formatRelative(result.checked_at)}</span>
                  </p>
                  <p className="mt-0.5 text-[11px] text-ink-muted">
                    {presentation.description}
                    {result.view === 'public'
                      ? ' Public view — detail needs an API key.'
                      : ''}
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p
                      className="tabular text-2xl leading-none font-semibold"
                      style={{
                        color: band === 'fast' || band === null ? 'var(--color-ink)' : latencyFill(band),
                      }}
                    >
                      {formatMs(result.elapsed_ms)}
                    </p>
                    <p className="text-[11px] text-ink-muted">round trip</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => probe.mutate({ target_ids: [result.target_id] })}
                    disabled={probe.isPending}
                    aria-label="Probe now"
                    className="rounded-md border p-2 text-ink-secondary transition-colors hover:bg-surface-sunken disabled:opacity-60"
                  >
                    <RefreshCw size={15} aria-hidden className={probe.isPending ? 'animate-spin' : undefined} />
                  </button>
                </div>
              </header>

              {result.error ? (
                <p className="border-t px-5 py-3 text-xs text-critical">{result.error}</p>
              ) : null}
            </article>

            {/* Availability + the full window, given the width the card could not spare. */}
            <section className="card grid gap-5 p-5 lg:grid-cols-[16rem_minmax(0,1fr)]">
              <div className="space-y-3">
                <Meter
                  label="Answered"
                  value={formatPct(history?.available_pct ?? null)}
                  ratio={(history?.available_pct ?? 0) / 100}
                  fill="var(--color-accent)"
                  threshold={0.99}
                  thresholdLabel="99%"
                />
                <Meter
                  label="Fully healthy"
                  value={formatPct(history?.healthy_pct ?? null)}
                  ratio={(history?.healthy_pct ?? 0) / 100}
                  fill={
                    (history?.healthy_pct ?? 100) >= 99
                      ? 'var(--color-good)'
                      : (history?.healthy_pct ?? 0) >= 90
                        ? 'var(--color-warning)'
                        : 'var(--color-critical)'
                  }
                />
                <dl className="grid grid-cols-3 gap-2 text-xs">
                  <Stat label="p50" value={formatMs(history?.latency_p50_ms ?? null)} />
                  <Stat label="p95" value={formatMs(history?.latency_p95_ms ?? null)} />
                  <Stat label="max" value={formatMs(history?.latency_max_ms ?? null)} />
                </dl>
                <dl className="grid grid-cols-2 gap-2 text-xs">
                  <Stat label="Samples" value={String(history?.sample_count ?? 0)} />
                  <Stat
                    label="Oldest"
                    value={history?.first_sample_at ? formatRelative(history.first_sample_at) : '—'}
                  />
                </dl>
              </div>

              <div>
                <h2 className="mb-2 text-xs font-semibold text-ink-secondary">
                  Status over the window
                </h2>
                <StatusTimeline
                  samplesInput={history?.samples ?? []}
                  height={40}
                  label={`${result.site_name} · ${result.env_label} · ${result.host}`}
                  caption={
                    history?.first_sample_at
                      ? `${history.sample_count} samples · since ${formatDateTime(history.first_sample_at)}`
                      : undefined
                  }
                />
              </div>
            </section>

            {issueEntries.length > 0 ? (
              <section className="card p-5">
                <h2 className="mb-2 text-sm font-semibold text-ink">
                  {issueEntries.length} issue{issueEntries.length === 1 ? '' : 's'} reported
                </h2>
                <ul className="space-y-1">
                  {issueEntries.map(([name, detail]) => (
                    <li key={name} className="flex gap-2 text-xs">
                      <span className="shrink-0 font-medium text-critical">{name}</span>
                      <span className="min-w-0 break-words text-ink-secondary">{detail}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            <section className="card overflow-hidden">
              <h2 className="border-b px-5 py-3 text-sm font-semibold text-ink">
                Checks
              </h2>
              <ServiceList
                result={result}
                samples={history?.samples ?? []}
                thresholds={thresholds}
                site={site}
                onCheckNow={probeNow}
                isChecking={probe.isPending}
              />
            </section>

            {queueSnapshot ? (
              <section className="card p-5">
                <h2 className="mb-3 text-sm font-semibold text-ink">Message queues</h2>
                <QueuePanel snapshot={queueSnapshot} />
              </section>
            ) : null}

            {result.raw ? (
              <section className="card overflow-hidden">
                <h2 className="border-b px-5 py-3 text-sm font-semibold text-ink">
                  Raw response
                </h2>
                <RawPayload payload={result.raw} />
              </section>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-ink-muted">{label}</dt>
      <dd className="tabular font-semibold text-ink">{value}</dd>
    </div>
  )
}
